<template>
  <div :id="$attrs['id']">
    <wizard v-if="obj" :steps="['Betrag', 'E-Mail']" :close-on-click-outside="true" @cancel="$emit('close')">
      <template slot-scope="slot">
        <div v-if="slot.activeStep === 1" class="col-12">
          <hm-table :hover="false"
                    min-width="600px"
                    columns="5"
                    grid-template-columns="4rem minmax(32rem, 3fr) 19rem minmax(16rem, 2fr) minmax(16rem, 2fr)">
            <template>
              <template slot="table-head">
                <div class="hm-table__th">{{ $t('finance.paymentRequestWizard.reservations.included') }}</div>
                <div class="hm-table__th">{{ $t('finance.paymentRequestWizard.reservations.label') }}</div>
                <div class="hm-table__th">{{ $t('finance.paymentRequestWizard.reservations.paymentAmount') }}</div>
                <div class="hm-table__th text-right">{{ $t('finance.paymentRequestWizard.reservations.balance') }}</div>
                <div class="hm-table__th text-right">{{ $t('finance.paymentRequestWizard.reservations.reservationTotal') }}</div>
              </template>
              <template v-for="detail in obj.folioPaymentDetails">
                <div class="hm-table__td" style="padding-top: 4px;"><hm-input-checkbox v-model="detail.additionalFields.include" :label="$t('finance.paymentRequestWizard.included')"/></div>
                <div class="hm-table__td cursor--pointer" @click="detail.additionalFields.include = !detail.additionalFields.include">Res. #{{ $options.filters.id2number(detail.parentId) }} // Zi: {{ detail.additionalFields.roomNumber || '-' }} // <span class="privacy--blur">{{ detail.additionalFields.displayName }}</span></div>
                <div class="hm-table__td">
                  <hm-input-money v-model="detail.amount"
                                  :currency="detail.currency"
                                  :disabled="!detail.additionalFields.include"
                                  :inline="true"
                                  :required="true"
                                  :label="$t('finance.paymentRequestWizard.reservations.paymentAmount')"
                                  class="hm-input--inline-block"
                                  validate="min_value:0">
                    <template slot="icon-inline-right" slot-scope="slot">
                      <input-icon-button icon="refresh" @click="resetAmount(detail)" />
                    </template>
                  </hm-input-money>
                </div>
                <div class="hm-table__td text-right">
                  {{ detail.additionalFields.balance | toCurrency }}
                </div>
                <div class="hm-table__td text-right">
                  {{ detail.additionalFields.totalIncludingTaxes | toCurrency }}
                </div>
              </template>
            </template>
            <template slot="table-foot">
              <div class="hm-table__td text--bold"></div>
              <div class="hm-table__td text--bold">{{ $t('finance.paymentRequestWizard.totalPaymentAmount') }}</div>
              <div class="hm-table__td text-right text--bold">{{ totalPaymentAmount | toCurrency }} {{ hotelSettings.details.currency }}</div>
              <div class="hm-table__td text-right"></div>
              <div class="hm-table__td text-right"></div>
            </template>
          </hm-table>
        </div>
        <div v-if="slot.activeStep === 2" class="col-12">
          <hm-card-inner :title="$t('finance.paymentRequestWizard.card.paymentOptions')">
            <hm-input-select v-model="obj.psp"
                             :label="$t('finance.paymentRequestWizard.paymentServiceProvider')"
                             :disabled-options="disabledPaymentServiceProviders"
                             name="psp"
                             enum-name="PaymentServiceProvider"
                             cols="12 6@tablet 3@desktop" />
            <!-- due date is required for payment method calculation -->
            <hm-input-date v-model="obj.dueDate"
                           :label="$t('finance.paymentRequestWizard.dueDate')"
                           name="dueDate"
                           cols="12 6@tablet 3@desktop" />
            <hm-input-select v-model="obj.depositIhsId"
                             :label="$t('finance.paymentRequestWizard.transactionCode')"
                             cols="12 6@tablet 3@desktop">
              <option v-for="item in hotelSettings.inHouseServices.all(obj.depositIhsId).filter(it => it.type === 'PrePayment')" :value="item.id" v-html="item.name[$shortLocale]"></option>
            </hm-input-select>
            <hm-input-select v-model="obj.setupType"
                             :disabled="['Manual', 'Fanz'].includes(obj.psp)"
                             :label="$t('finance.paymentRequestWizard.setupType')"
                             :help="$t('finance.paymentRequestWizard.setupType_help')"
                             :disabled-options="totalPaymentAmount === 0 ? ['OneOff'] : []"
                             name="setupType"
                             enum-name="PaymentSetupType"
                             cols="12 6@tablet 3@desktop" />
          </hm-card-inner>
          <hm-card-inner title="E-Mails">
            <!-- TODO -->
            <guest-search :required="false"
                          v-model="obj.guestProfileId"
                          :disabled="true"
                          :label="$t('finance.paymentRequestWizard.guestProfile')"
                          cols="12 6@tablet start-1"
                          rows="start-1@mobile" />
            <hm-select id="email"
                       v-model="email"
                       :options="emails"
                       :taggable="true"
                       :label="$t('finance.paymentRequestWizard.email')"
                       :parent-errors="errors"
                       value-expression="email"
                       label-expression="${option.email}"
                       name="email"
                       data-vv-as="E-Mail"
                       cols="12 6@tablet start-1"
                       @blur="$v.email.$touch()">
              <template slot="option" slot-scope="props">
                <table>
                  <tr>
                    <td v-html="props.option.email || props.option.name"></td>
                  </tr>
                </table>
              </template>
            </hm-select>
            <hm-input-select id="requestTemplate"
                             v-model="requestEmail"
                             :label="$t('finance.paymentRequestWizard.requestTemplate')"
                             :parent-errors="errors"
                             name="requestEmail"
                             cols="12 6@tablet start-1"
                             @blur="$v.requestEmail.$touch()">
              <option :value="null"></option>
              <option v-for="template in templates" :value="template.id">{{ template.name }}</option>
            </hm-input-select>
            <hm-input-select id="confirmationTemplate"
                             v-model="confirmationEmail"
                             :label="$t('finance.paymentRequestWizard.confirmationTemplate')"
                             :parent-errors="errors"
                             name="confirmationEmail"
                             cols="12 6@tablet start-1"
                             @blur="$v.confirmationEmail.$touch()">
              <option :value="null"></option>
              <option v-for="template in templates" :value="template.id">{{ template.name }}</option>
            </hm-input-select>
          </hm-card-inner>
          <hm-card-inner :title="$t('finance.paymentRequestWizard.card.summary')">
            <label-value-dotted :value="totalPaymentAmount | toCurrency" :unit="hotelSettings.details.currency" :label="$t('finance.paymentRequestWizard.totalPaymentAmount')" />
          </hm-card-inner>
        </div>
      </template>
      <template slot="buttons" slot-scope="slot">
        <hm-button v-if="slot.activeStep === 1"
                   class="btn--no-margin btn--red"
                   cols="6"
                   icon-left="close"
                   @click="$emit('close')">{{ $t('finance.paymentRequestWizard.modal.cancel') }}</hm-button>
        <hm-button v-if="slot.activeStep > 1"
                   class="btn--no-margin"
                   cols="6"
                   icon-left="back-1"
                   @click="slot.changeStep(slot.activeStep - 1)">{{ $t('finance.paymentRequestWizard.modal.back') }}</hm-button>
        <hm-button v-if="slot.activeStep < slot.steps"
                   :disabled="nextDisabled"
                   cols="6"
                   class="btn--no-margin"
                   icon-right="forward-1"
                   @click="nextStep(slot)">{{ $t('finance.paymentRequestWizard.modal.next') }}</hm-button>
        <hm-button v-if="slot.activeStep === slot.steps"
                   :disabled="completeDisabled"
                   cols="6"
                   class="btn--no-margin btn--green"
                   icon-left="cash"
                   @click="createPaymentRequest()">{{ $t('finance.paymentRequestWizard.modal.submit') }}</hm-button>
      </template>
    </wizard>
  </div>
</template>

<script>

import { cloneDeep, find, first, head, isEmpty, isNil, min, minBy, sortBy, sumBy } from 'lodash';
import printJS from 'print-js';
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import UpdateHotelSettingsMixin from '../../../mixins/UpdateHotelSettingsMixin';
import TimerMixin from '../../../mixins/TimerMixin';
import HmInputToggle from '../inputs/HmInputToggle';
import HmInputMoney from '../inputs/HmInputMoney';
import HmCardInner from '../layout/HmCardInner';
import HmInputSelect from '../inputs/HmInputSelect';
import HmTable from '../table/HmTable';
import HmInputCheckbox from '../inputs/HmInputCheckbox';
import ValidateMixin from '../../../mixins/ValidateMixin';
import { email } from 'vuelidate/lib/validators';

export default {
  name: 'PaymentRequestWizard',
  components: { HmInputCheckbox, HmTable, HmInputSelect, HmCardInner, HmInputMoney, HmInputToggle },

  mixins: [HotelmeisterMixin, UpdateHotelSettingsMixin, TimerMixin, ValidateMixin],

  props: {
    type: {
      type: String,
      default: 'Deposit',
    },
    confirmationNumber: {
      type: String,
      default: null,
    },
    guestProfileId: {
      type: String,
      default: null,
      required: false,
    },
    roomReservations: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      obj: null,
      templates: [],
      emails: [],
      email: null,
      requestEmail: null,
      confirmationEmail: null,
      stripeConfig: { enabled: false },
      mollieConfig: { enabled: false },
      fanzConfig: { enabled: false },
    };
  },

  validations() {
    return {
      email: this.$hv__createValidators('required|email', this.$t('finance.paymentRequestWizard.email')),
      requestEmail: this.$hv__createValidators('required', this.$t('finance.paymentRequestWizard.requestTemplate')),
      confirmationEmail: this.$hv__createValidators('required', this.$t('finance.paymentRequestWizard.confirmationTemplate')),
    };
  },

  computed: {
    disabledPaymentServiceProviders: function () {
      const ret = [];

      if (!this.hasLicense('InterfaceStripe') || this.stripeConfig.enabled !== true) ret.push('Stripe');
      if (!this.hasLicense('InterfaceMollie') || this.mollieConfig.enabled !== true) ret.push('Mollie');
      if (!this.hasLicense('InterfaceFanz') || this.fanzConfig.enabled !== true) ret.push('Fanz');

      if (this.totalPaymentAmount === 0) {
        ret.push('Fanz');
        ret.push('Manual');
      }

      return ret;
    },
    totalPaymentAmount: function () {
      if (isNil(this.obj)) return 0.0;
      return sumBy(this.obj.folioPaymentDetails.filter(it => it.additionalFields.include), 'amount');
    },
    include: function () {
      if (isNil(this.obj)) return [];
      else return this.obj.folioPaymentDetails.map(it => it.additionalFields.include);
    },
    nextDisabled() {
      return isEmpty(this.obj.folioPaymentDetails) || !this.obj.folioPaymentDetails.some(it => it.additionalFields.include === true) || this.obj.folioPaymentDetails.filter(it => it.additionalFields.include === true).some(it => it.amount < 0);
    },
    completeDisabled() {
      if (isEmpty(this.obj.psp)) return true;
      if (isNil(this.obj.dueDate)) return true;
      if (isEmpty(this.obj.depositIhsId)) return true;

      if (!isEmpty(this.email) && !email(this.email)) return true;

      return false;
    },
  },

  watch: {
    'obj.dueDate': function (newVal) {
      const hours = this.$moment(newVal, 'YYYYMMDD').endOf('day').diff(this.$moment(), 'minutes');
      this.obj.expiry = `PT${Math.max(1, hours)}M`;
    },
    totalPaymentAmount: function (newVal) {
      if (newVal === 0) {
        this.obj.setupType = 'First';
      } else {
        this.obj.setupType = 'OneOff';
      }
    },
    'obj.folioPaymentDetails': {
      handler: function () {
        if (this.totalPaymentAmount === 0) {
          this.obj.setupType = 'First';
        } else {
          this.obj.setupType = 'OneOff';
        }
      },
      deep: true,
    },
    'obj.psp': function (newVal) {
      switch (newVal) {
        case 'Manual':
        case 'Fanz':
          this.obj.setupType = 'OneOff';
          break;
        default:
          // do nothing
          break;
      }
    },
    include: function (newVal, oldVal) {
      if (!isNil(newVal) && !isNil(oldVal) && newVal.length === oldVal.length) {
        newVal.forEach((it, idx) => {
          if (!it && it !== oldVal[idx]) this.obj.folioPaymentDetails[idx].amount = 0.0;
          if (it && it !== oldVal[idx]) this.resetAmount(this.obj.folioPaymentDetails[idx]);
        });
      }
    },
  },

  created: function () {
    this.createObj();
    this.loadPspSettings();
    this.updateTemplates();
    this.updateEmails();
  },

  methods: {
    nextStep(slot) {
      slot.changeStep(slot.activeStep + 1);

      try {
        const enabledPsps = this.enums.PaymentServiceProvider.filter(it => !this.disabledPaymentServiceProviders.includes(it));

        const fallbackPsp = enabledPsps.includes('Manual') ? 'Manual' : (first(enabledPsps) || 'Stripe')

        this.obj.psp = find(enabledPsps, (o) => o !== 'Manual' ) || fallbackPsp
      } catch (e) {
        this.$rollbar.warn(e);
      }
    },
    createObj() {
      const today = this.$moment().format('YYYYMMDD');

      const depositIhsId = this.hotelSettings.details.paymentRequestSettings.depositConfiguration.defaultDepositIhsId;

      const earliestCheckInDate = minBy(this.roomReservations.map(it => it.checkInDate), it => parseInt(it, 10));
      let dueDate = this.$moment(earliestCheckInDate, 'YYYYMMDD').add(-1, 'days');

      const defaultPaymentPeriod = this.$moment.duration(this.hotelSettings.details.paymentRequestSettings.depositConfiguration.defaultDepositPaymentTerm || 'P7D');

      if (this.$moment().add(defaultPaymentPeriod).isBefore(dueDate)) {
        dueDate = this.$moment().add(defaultPaymentPeriod);
      }

      if (dueDate.isBefore(this.$moment())) {
        dueDate = this.$moment();
      }

      const hours = dueDate.endOf('day').diff(this.$moment(), 'minutes');

      this.requestEmail = this.hotelSettings.details.paymentRequestSettings.depositConfiguration.depositRequestEmailTemplateId;
      this.confirmationEmail = this.hotelSettings.details.paymentRequestSettings.depositConfiguration.depositConfirmationEmailTemplateId;

      const req = {
        setupType: 'OneOff',
        psp: 'Stripe',
        expiry: `PT${Math.max(60, hours)}M`,
        dueDate: dueDate.format('YYYYMMDD'),
        email: null,
        depositIhsId: depositIhsId,
        guestProfileId: this.guestProfileId,
        confirmationNumber: this.confirmationNumber,
        reservationId: null,
        folioPaymentDetails: this.roomReservations.map((it) => {
          let amount = 0.0;
          let resetAmount = 0.0;

          if (isNil(it.balance)) it.balance = 0.0;

          switch (it.status) {
            case 'InHouse':
            case 'Departed':
            case 'NoShow':
              amount = -it.balance;
              resetAmount = -it.balance;
              break;
            case 'Cancelled':
            case 'CreationFailed':
            case 'RequestExpired':
              amount = 0.0;
              resetAmount = Math.max(-it.balance, 0.0);
              break;
            default:
              if (it.balance === 0.0 && it.prepayment.amount > 0) {
                amount = it.prepayment.amount;
              }

              // balance is negative (customer owes as money)
              if (it.balance > 0.0 && (it.prepayment.amount - it.balance) > 0.0) {
                amount = it.prepayment.amount - it.balance;
              } else if (it.balance > 0.0 && (it.prepayment.amount - it.balance) <= 0.0) {
                amount = it.totalIncludingTaxes - it.balance;
              }

              // balance is negative (customer owes as money)
              if (it.balance < 0.0) {
                amount = it.totalIncludingTaxes - it.balance;
              }

              resetAmount = amount <= 0.0 ? it.totalIncludingTaxes - it.balance : amount;
              break;
          }
          amount = Math.max(amount, 0.0);
          amount = parseFloat(amount.toFixed(2));

          return {
            parentId: it.reservationId,
            amount: amount,
            currency: this.hotelSettings.details.currency,
            additionalFields: {
              include: amount > 0.0,
              resetAmount: Math.max(resetAmount, 0.0),
              status: it.status,
              deposit: it.prepayment.amount,
              balance: it.balance,
              total: it.total,
              totalIncludingTaxes: it.totalIncludingTaxes,
              displayName: it.stayDetails.displayName,
              arrivalDate: it.checkInDate,
              departureDate: it.checkOutDate,
              roomNumber: it.roomNumber,
              roomTypeId: it.stayDetails.roomTypeId,
              ratePlanId: it.stayDetails.ratePlanId,
            },
          };
        }),
      };

      this.obj = req;
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Email'))
        .then((response) => {
          let filteredTemplates = response.data.data;

          filteredTemplates = filteredTemplates.filter(it => it.type !== 'ReservationConfirmationEmail');
          filteredTemplates = filteredTemplates.filter(it => it.type !== 'GuestOfferEmail');

          filteredTemplates = sortBy(filteredTemplates, ['name']);

          this.templates = filteredTemplates;
        });
    },
    updateEmails() {
      this.axios.get(this.serviceUrls.reservationGroup.email(this, this.confirmationNumber), {
        params: {
          type: 'autocomplete',
        },
      })
        .then((response) => {
          this.emails = response.data.data.map(it => ({ email: it }));

          if (isEmpty(this.email) && !isEmpty(this.emails) && this.emails.length > 0) {
            this.$nextTick(() => {
              this.email = this.emails[0].email;
            });
          }
        });
    },
    createPaymentRequest() {
      const req = cloneDeep(this.obj);
      req.folioPaymentDetails = req.folioPaymentDetails.filter(it => it.additionalFields.include);
      req.folioPaymentDetails.forEach(it => delete it.additionalFields);

      req.email = this.email;
      req.paymentRequestEmailTemplateId = this.requestEmail;
      req.paymentConfirmationEmailTemplateId = this.confirmationEmail;

      this.axios.post(this.serviceUrls.paymentRequest.create(this, 'deposit'), req)
        .then((_) => {
          this.showSuccessMessage();
          this.$emit('close');
        })
        .catch((error) => {
          this.showMessages(error.response);
        });
      // http://localhost:8080/rest/redirect/paymentRequest/537684c4cb/whXae3XLrJ
    },
    resetAmount(folioPaymentDetail) {
      folioPaymentDetail.amount = folioPaymentDetail.additionalFields.resetAmount || 0.0;
    },
    loadPspSettings() {
      if (this.hasLicense('InterfaceStripe') && this.hotelSettings.interfaces.stripe.enabled === true) {
        this.axios.get(this.serviceUrls.interfaces.stripe.get(this, null))
          .then((response) => {
            this.stripeConfig = response.data.data;
          });
      }
      if (this.hasLicense('InterfaceMollie') && this.hotelSettings.interfaces.mollie.enabled === true) {
        this.axios.get(this.serviceUrls.interfaces.mollie.get(this, null))
          .then((response) => {
            this.mollieConfig = response.data.data;
          });
      }
      if (this.hasLicense('InterfaceFanz') && this.hotelSettings.interfaces.fanz.enabled === true) {
        this.axios.get(this.serviceUrls.interfaces.fanz.get(this, null))
          .then((response) => {
            this.fanzConfig = response.data.data;
          });
      }
    },
  },
};
</script>

<style lang="scss">
  @import '~styles/import';

</style>



// WEBPACK FOOTER //
// src/components/lib/finance/PaymentRequestWizard.vue